import { StyleSheet } from 'react-native';

import type { UiTheme } from "../theme";

// Styles for the lifted ProgramsPlanView + its rows (Follow+Publish
// lift; was apps/mobile programsSectionStyles)
// (Track R — split out so ProgramRow can live in its own file without a
// circular import back into _ProgramsSection).
export function makePlanStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    wrap: {
      paddingHorizontal: s.s5,
      paddingTop: s.s2,
      paddingBottom: s.s4,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
      gap: s.s3,
    },
    head: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    // One-line hint right under the section header. Disambiguates
    // "programmi" from "piani" — the user fed back that the two
    // sections sat unlabeled and ran together visually.
    hint: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      lineHeight: t.sizes.sm * 1.4,
    },
    // Explanatory placeholder card for the (currently sparse) "Programs"
    // section — reads as intentional, not empty (Antenore 2026-06-18).
    placeholder: {
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface2,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      marginBottom: s.s2,
    },
    placeholderText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      lineHeight: t.sizes.sm * 1.45,
    },
    // Inline day/week chips on the run card. WRAP (not horizontal scroll) so a
    // phone shows them on as many rows as needed without fighting the carousel
    // swipe; each chip is a ≥44pt tap target (a11y).
    chipWrap: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s2,
      marginBottom: s.s2,
    },
    // Sub-header inside a section (e.g. "di Metria" above the shelf).
    subTitle: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      paddingTop: s.s2,
    },
    // "My programs" — the owner's not-followed programs (drafts +
    // published), in a collapsed-by-default section with an always-
    // visible count (Follow+Publish model). Rows are quiet: muted name,
    // status chip, text actions.
    myProgramRow: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
      paddingVertical: s.s2,
    },
    myProgramHead: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s.s3,
    },
    myProgramName: {
      flex: 1,
      color: p.ink2,
      fontSize: t.sizes.base,
      fontWeight: '600',
    },
    myProgramActions: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: s.s4,
    },
    // Status chip: text + colour, never colour alone (a11y). Draft =
    // warn ink; published = muted ink. Neutral surface keeps contrast.
    statusChip: {
      paddingHorizontal: s.s3,
      paddingVertical: 4,
      borderRadius: r.r4,
      backgroundColor: p.surface2,
    },
    // sm, not xs: the chip carries decision-relevant state (Draft /
    // Following / difficulty), not a decorative micro-label (UX audit
    // HR-S3 — legibility under the system font scale).
    statusChipText: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: '700',
    },
    statusChipDraft: {},
    statusChipDraftText: {
      color: p.warn,
    },
    // Collapsible "My programs (n)" header — count always visible so an
    // unfollowed program visibly lands here instead of "disappearing".
    myProgramsToggle: {
      minHeight: 44,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      paddingTop: s.s2,
    },
    // Card root — position:relative so the ⋯ dropdown + its dismiss backdrop
    // anchor to the card. gap keeps the compact rows evenly spaced.
    cardRoot: {
      position: 'relative',
      paddingVertical: s.s2,
      gap: s.s2,
    },
    programHead: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s.s3,
    },
    programName: {
      flex: 1,
      // minWidth:0 lets the flex child shrink below its intrinsic text width
      // so a long name never pushes the card past the screen (Antenore
      // 2026-06-26). Compacted from `lg` to `md` (2026-07-01: hero was too big).
      minWidth: 0,
      color: p.ink,
      fontSize: t.sizes.lg,
      fontWeight: '700',
      letterSpacing: -0.2,
    },
    // One-line selected-slot subtitle (replaces the big "Next up" card).
    selectedLine: {
      color: p.ink2,
      fontSize: t.sizes.base,
      fontWeight: '600',
    },
    // #393: the two target-coverage lines under the name, held together.
    coverage: { gap: s.s1 },
    // Mono digits sized to the surrounding text so the font scale cannot
    // make the number taller than the words (#393 ritocchi).
    coverageNum: {
      fontFamily: t.fonts.mono,
      fontVariant: ['tabular-nums'],
      fontSize: t.sizes.base,
      lineHeight: Math.round(t.sizes.base * 1.4),
    },
    // Uncovered muscles: same size as the other lines, wraps, never truncated.
    coverageUncovered: {
      color: p.ink2,
      fontSize: t.sizes.base,
    },
    // "Next:" line: lighter than the semibold coverage line above it.
    nextLine: {
      color: p.ink2,
      fontSize: t.sizes.base,
      fontWeight: '400',
    },
    // A chip group = an inline label + the wrapping chips.
    chipRow: {
      gap: s.s2,
    },
    chipRowLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    // Actions — ONE row of equal-width ICON buttons: play Start (brand) + View
    // (list) / Edit / Unfollow (x), all the same size, spaced dynamically
    // (Antenore 2026-07-01: icons not labels, uniform size + spacing). Each is
    // flex:1 so they share the width evenly and re-space on any card width.
    actionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      marginTop: s.s1,
    },
    // The primary action — brand fill, the one orange on the card.
    startIconBtn: {
      flexGrow: 1,
      flexBasis: 0,
      minHeight: 40,
      borderRadius: r.r3,
      backgroundColor: p.brand500,
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Secondary actions — neutral square, same size as Start.
    actionIconBtn: {
      flexGrow: 1,
      flexBasis: 0,
      minHeight: 40,
      borderRadius: r.r3,
      backgroundColor: p.surface2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Disabled Start flips bg to surface2 (near-white); inkOnBrand text would
    // vanish against it, so override to a readable muted ink.
    primaryStartTextDisabled: { color: p.ink3 },
    startButtonDisabled: { backgroundColor: p.surface2 },
    startButtonPressed: { opacity: 0.7 },
    programLast: {
      color: p.ink2,
      fontSize: t.sizes.sm,
    },
    // (rowAction/changeLink*/deleteLinkText/iconLink retired with HR-S4:
    // the text-action idiom lives in the shared <RowAction> primitive now.)
    pickerLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    chipPressed: { opacity: 0.7 },
  });
}
